import type { HTMLAttributes, ReactNode } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import type { PoolChoice } from '../../../Services/OrderWizard/pools.ts'
import { poolTitle } from '../../../Services/OrderWizard/pools.ts'
import { plural } from '../../../Util/plural.ts'

export const SectionHeading = ({ className = '', children }: { className?: string; children: ReactNode }) => (
  <h3 className={`${className} text-sm font-semibold uppercase tracking-wide text-slate-500`}>{children}</h3>
)

export type CalloutTone = 'warning' | 'blocker'

/** The wizard's left-bordered frame for what needs the lab: amber to check, red to fix. */
export const Callout = ({
  as: Element = 'div',
  tone = 'warning',
  className = '',
  children,
  ...attributes
}: HTMLAttributes<HTMLElement> & { as?: 'div' | 'li' | 'section'; tone?: CalloutTone; children: ReactNode }) => (
  <Element
    className={`${className} border border-slate-200 border-l-4 ${tone === 'blocker' ? 'border-l-red-400' : 'border-l-amber-400'}`}
    {...attributes}
  >
    {children}
  </Element>
)

const WARNING_LAYOUT = {
  /* the icon runs with the text, which wraps under it */
  inline: { frame: 'text-xs text-amber-700', icon: 'mr-1 inline h-3 w-3 align-[-1px]' },
  /* the icon beside text of several lines */
  start: { frame: 'flex items-start gap-1 text-xs text-amber-700', icon: 'mt-0.5 h-3 w-3 shrink-0' },
  /* a short note beside a value */
  badge: { frame: 'inline-flex items-center gap-1 text-xs text-amber-700', icon: 'h-3 w-3' }
}

export const InlineWarning = ({
  layout,
  children,
  ...attributes
}: HTMLAttributes<HTMLElement> & { layout: keyof typeof WARNING_LAYOUT; children: ReactNode }) => {
  const { frame, icon } = WARNING_LAYOUT[layout]
  const Element = layout === 'badge' ? 'span' : 'p'
  return (
    <Element className={frame} {...attributes}>
      <ExclamationTriangleIcon className={icon} />
      {layout === 'badge' && ' '}
      {children}
    </Element>
  )
}

/** A tag in front of a primer, or one of a sample's tags. */
export const TagChip = ({ mono = false, children }: { mono?: boolean; children: ReactNode }) => (
  <span className={`rounded bg-sky-100 px-0.5 ${mono ? 'font-mono ' : ''}text-sky-900`}>{children}</span>
)

export const NEW_POOL = 'new'

/** A pool to move to: every pool with its size, or a new one. */
export const PoolSelect = ({
  pools,
  value,
  onChange,
  ...attributes
}: { pools: readonly PoolChoice[]; value: string; onChange: (value: string) => void } & Pick<
  HTMLAttributes<HTMLSelectElement>,
  'aria-label'
>) => (
  <select className="select select-bordered select-xs" value={value} onChange={(event) => onChange(event.target.value)} {...attributes}>
    <option value="">Pool…</option>
    {pools.map((pool) => (
      <option key={pool.pool} value={pool.pool}>
        {poolTitle(pool)} ({plural(pool.items, 'item')})
      </option>
    ))}
    <option value={NEW_POOL}>New pool</option>
  </select>
)
